import { PATHS } from '@/config/paths';

import type { MenuTreeNode } from '../types/menu.types';

/**
 * Đường dẫn nội bộ thuộc route Vận hành (`/ops`, `/ops/...`).
 */
export function isOpsMenuPath(path: string | null | undefined): boolean {
  if (!path || typeof path !== 'string') return false;
  const p = path.trim();
  if (p.startsWith('http')) return false;
  return p === PATHS.OPS || p.startsWith(`${PATHS.OPS}/`);
}

/**
 * Đường dẫn thường gặp ở BE cho mục điều hành / quản trị (không phải shell bản đồ).
 * Spec ví dụ: `/admin/data/netcdf` — nhiều team đặt Audit, IoT, báo cáo dưới `/admin/...`.
 */
function isAdminOrOperationsPath(path: string | null | undefined): boolean {
  if (!path || typeof path !== 'string') return false;
  const p = path.trim();
  if (p.startsWith('http')) return false;
  return p.startsWith('/admin') || p.startsWith('/operations');
}

/**
 * Route nội bộ thuộc shell Vận hành nhưng không prefix `/ops` (vd. `/data/upload`, `/iot/...`).
 * Dùng cho guard menu động và {@link extractOpsMenuTree}.
 */
export function isDetachedOpsRoutePath(path: string | null | undefined): boolean {
  if (!path || typeof path !== 'string') return false;
  const p = path.trim();
  if (p.startsWith('http')) return false;
  return (
    p.startsWith('/analysis/') ||
    p.startsWith('/data/') ||
    p.startsWith('/alerts/') ||
    p.startsWith('/reports/') ||
    p.startsWith('/iot/')
  );
}

/** BE đặt ngoài `/ops` nhưng thuộc vận hành — sidebar `/ops`, không header bản đồ (gồm `/iot/...`). */
function isDetachedOpsModulePath(path: string | null | undefined): boolean {
  return isDetachedOpsRoutePath(path);
}

/** Node gắn scope qua metadata (khi BE không dùng prefix rõ ràng). */
function isMetadataOperationsShell(node: MenuTreeNode): boolean {
  const m = node.metadata;
  if (!m || typeof m !== 'object') return false;
  const rec = m as Record<string, unknown>;
  const scope = rec.nav_scope;
  if (scope === 'ops' || scope === 'operations' || scope === 'admin') return true;
  const shell = rec.shell;
  if (shell === 'ops' || shell === 'operations') return true;
  return false;
}

/**
 * Mục thuộc khu Vận hành / quản trị — **không** hiển thị trên header bản đồ;
 * dùng trong sidebar Ops (`extractOpsMenuTree`).
 */
export function belongsToOperationsShell(node: MenuTreeNode): boolean {
  return (
    isOpsMenuPath(node.path) ||
    isAdminOrOperationsPath(node.path) ||
    isDetachedOpsModulePath(node.path) ||
    isMetadataOperationsShell(node)
  );
}

/**
 * Menu hiển thị trên shell bản đồ: bỏ Vận hành / admin / metadata / module tách (`/analysis`, `/data`, …).
 */
export function filterNonOpsMenuTree(tree: MenuTreeNode[]): MenuTreeNode[] {
  return tree
    .filter((n) => n.is_active)
    .map((n) => ({
      ...n,
      children: filterNonOpsMenuTree(n.children ?? []),
    }))
    .filter((n) => !belongsToOperationsShell(n))
    .sort((a, b) => a.sort_order - b.sort_order);
}

/** Strip trailing slashes so internal paths match `PATHS` literals. */
function normalizeInternalMenuPath(path: string): string {
  const p = path.trim();
  if (!p) return '';
  return p.replace(/\/+$/, '') || '/';
}

/** Hide `/dashboard` and `/map/iot` on the map shell header (redundant shortcuts). */
function isMapShellHeaderExcludedPath(path: string | null | undefined): boolean {
  if (!path || typeof path !== 'string') return false;
  if (path.trim().startsWith('http')) return false;
  const norm = normalizeInternalMenuPath(path);
  return norm === PATHS.DASHBOARD || norm === PATHS.MAP_IOT;
}

/**
 * Same as {@link filterNonOpsMenuTree} plus removal of `PATHS.DASHBOARD` and `PATHS.MAP_IOT`.
 * Used only by `DashboardShellMenuLinks`; dashboard sidebar keeps {@link filterNonOpsMenuTree}.
 */
export function filterMapShellHeaderMenuTree(tree: MenuTreeNode[]): MenuTreeNode[] {
  return tree
    .filter((n) => n.is_active)
    .map((n) => ({
      ...n,
      children: filterMapShellHeaderMenuTree(n.children ?? []),
    }))
    .filter((n) => !belongsToOperationsShell(n) && !isMapShellHeaderExcludedPath(n.path))
    .sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * Cây con cho sidebar Vận hành: node khớp `belongsToOperationsShell` hoặc nhánh con có.
 */
export function extractOpsMenuTree(tree: MenuTreeNode[]): MenuTreeNode[] {
  const out: MenuTreeNode[] = [];
  for (const n of tree) {
    if (!n.is_active) continue;
    if (belongsToOperationsShell(n)) {
      out.push(n);
      continue;
    }
    const sub = extractOpsMenuTree(n.children ?? []);
    out.push(...sub);
  }
  return out.sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * Cây menu có node (lá) trùng path `target` (alias `/iot/stations` vs `/ops/stations`).
 */
export function menuTreeHasExactPath(tree: MenuTreeNode[] | undefined, target: string): boolean {
  if (!tree?.length) return false;
  const norm = target.trim();
  for (const n of tree) {
    if (!n.is_active) continue;
    const p = typeof n.path === 'string' ? n.path.trim() : '';
    if (p && !p.startsWith('http') && p === norm) return true;
    if (menuTreeHasExactPath(n.children ?? [], target)) return true;
  }
  return false;
}
